import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { useAlerts } from '@/context/AlertContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import POEditModal from '@/components/purchaseOrder/POEditModal';
import { 
  Building2, 
  Package, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  MessageSquare,
  ArrowLeft,
  AlertTriangle,
  User,
  Clock,
  FileText,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Edit,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

interface ApprovalAction {
  type: 'approve' | 'reject' | 'request_changes';
  remarks?: string;
  approvedBy?: number;
  approvedAt?: string;
}

const POApprovalPage = () => {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const { state, actions } = useProcurement();
  const { addAlert } = useAlerts();
  
  const [po, setPO] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [approvalHistory, setApprovalHistory] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    if (poId) {
      loadPO();
      loadApprovalHistory();
    }
  }, [poId]);

  // Auto-enable approval actions for CREATED and PENDING_APPROVAL status POs
  useEffect(() => {
    if (po && (po.status === 'CREATED' || po.status === 'Created/New' || po.status === 'PENDING_APPROVAL')) {
      setHasReviewed(true);
    }
  }, [po]);

  const loadPO = async () => {
    try {
      setLoading(true);
      // In a real app, this would be an API call
      const foundPO = state.purchaseOrders.find(p => p.id === parseInt(poId!));
      if (foundPO) {
        setPO(foundPO);
      } else {
        // Only navigate away if we're sure the PO doesn't exist
        // Wait a bit for state to update in case of recent changes
        setTimeout(() => {
          const retryPO = state.purchaseOrders.find(p => p.id === parseInt(poId!));
          if (!retryPO) {
            toast.error('Purchase Order not found');
            navigate('/purchase-orders');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error loading PO:', error);
      toast.error('Failed to load Purchase Order');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovalHistory = async () => {
    try {
      // Mock approval history - in real app, this would come from API
      const mockHistory = [
        {
          id: 1,
          action: 'created',
          performedBy: 1,
          performedAt: new Date().toISOString(),
          remarks: 'Initial PO creation'
        }
      ];
      setApprovalHistory(mockHistory);
    } catch (error) {
      console.error('Error loading approval history:', error);
    }
  };

  const handleReviewPO = () => {
    setHasReviewed(true);
    toast.success('PO details reviewed. Approval actions are now enabled.');
  };

  const handleApprovalAction = async (action: 'approve' | 'reject' | 'request_changes') => {
    if (action === 'request_changes' && !remarks.trim()) {
      toast.error('Please provide remarks for the requested changes');
      return;
    }

    setProcessing(true);
    try {
      const approvalData: ApprovalAction = {
        type: action,
        remarks: action === 'request_changes' ? remarks : undefined,
        approvedBy: state.currentUser?.id || 1,
        approvedAt: new Date().toISOString()
      };

      // Call appropriate approval function
      let success = false;
      const userId = state.currentUser?.id || 1;
      
      switch (action) {
        case 'approve':
          success = await actions.approvePurchaseOrder(po!.id, userId);
          // After approval, PO status changes to APPROVED
          break;
        case 'reject':
          success = await actions.rejectPurchaseOrder(po!.id, userId, remarks);
          if (success) {
            // Create alert for rejected PO
            addAlert({
              type: 'system',
              message: `Purchase Order ${po!.poNumber} has been rejected`,
              invoiceId: 0, // No invoice yet for rejected PO
              poId: po!.poNumber,
              severity: 'high',
              section: 'purchase-orders',
              data: {
                poNumber: po!.poNumber,
                vendorName: state.vendors.find(v => v.id === po!.vendorId)?.name || 'Unknown',
                rejectionReason: remarks,
                rejectedBy: state.currentUser?.name || 'Unknown',
                rejectedAt: new Date().toISOString()
              }
            });
          }
          break;
        case 'request_changes':
          success = await actions.requestPurchaseOrderChanges(po!.id, userId, remarks);
          break;
        default:
          success = false;
      }

      if (success) {
        toast.success(`PO ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'sent back for revision'} successfully`);
        // Update local PO state to reflect status changes
        const updatedPO = state.purchaseOrders.find(p => p.id === po!.id);
        if (updatedPO) {
          setPO(updatedPO);
        }
        
        // Redirect back to Purchase Orders page after successful action
        setTimeout(() => {
          navigate('/purchase-orders');
        }, 1500); // Wait 1.5 seconds to show the success message
      } else {
        toast.error('Failed to update PO status');
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('Failed to process approval action');
    } finally {
      setProcessing(false);
      setShowRemarks(false);
      setRemarks('');
    }
  };

  const handleEditPO = () => {
    setShowEditModal(true);
  };

  const handleSavePO = async (updatedData: any) => {
    try {
      // Update PO data
      const success = await actions.updatePurchaseOrder(po!.id, updatedData);
      
      if (success) {
        // Update local state
        setPO(updatedData);
        
        // Add revision entry to history
        const revisionEntry = {
          id: Date.now(),
          action: 'revised',
          performedBy: state.currentUser?.id || 1,
          performedAt: new Date().toISOString(),
          remarks: 'PO revised based on feedback'
        };
        setApprovalHistory(prev => [revisionEntry, ...prev]);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving PO:', error);
      return false;
    }
  };

  const getVendorName = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : 'Unknown Vendor';
  };

  const getVendorDetails = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor;
  };

  const getSKUName = (skuId: number) => {
    const sku = state.skus.find(s => s.id === skuId);
    return sku ? sku.name : `SKU ${skuId}`;
  };

  const getSKUCode = (skuId: number) => {
    const sku = state.skus.find(s => s.id === skuId);
    return sku ? sku.skuCode : `SKU-${skuId}`;
  };

  const getCurrentStock = (skuId: number) => {
    const sku = state.skus.find(s => s.id === skuId);
    return sku ? sku.currentStock : 0;
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "CREATED":
        return "outline";
      case "APPROVED":
        return "default";
      case "REJECTED":
        return "destructive";
      case "NEEDS_REVISION":
      case "CHANGES_REQUESTED":
        return "secondary";
      case "Draft":
        return "outline";
      case "Partly Received":
      case "Partly Accepted":
        return "secondary";
      case "Fully Rejected":
        return "destructive";
      case "Fully Accepted":
        return "default";
      case "FULLY_RECEIVED":
        return "default";
      case "FULLY_REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStockStatus = (currentStock: number, requestedQty: number) => {
    if (currentStock >= requestedQty) {
      return { status: 'sufficient', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    } else if (currentStock > 0) {
      return { status: 'low', color: 'text-yellow-600', bg: 'bg-yellow-50', icon: AlertTriangle };
    } else {
      return { status: 'out', color: 'text-red-600', bg: 'bg-red-50', icon: XCircle };
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading Purchase Order...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!po) {
    return (
      <AppLayout>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Purchase Order Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested purchase order could not be found.</p>
          <Button onClick={() => navigate('/purchase-orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Purchase Orders
          </Button>
        </div>
      </AppLayout>
    );
  }

  const vendor = getVendorDetails(po.vendorId);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/purchase-orders')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">PO Approval</h1>
              <p className="text-muted-foreground">Review and approve purchase order {po.poNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {po.status === 'APPROVED' ? (
              <span className="badge bg-success text-white px-2 py-1 rounded text-sm">
                {po.status}
              </span>
            ) : (
              <Badge variant={getStatusVariant(po.status)} className="text-sm">
                {po.status}
              </Badge>
            )}
            {po.needsRevision && (
              <Badge variant="destructive" className="text-sm">
                Needs Revision
              </Badge>
            )}
          </div>
        </div>

        {/* Review Section - Only show for non-CREATED and non-PENDING_APPROVAL status POs */}
        {!hasReviewed && po && po.status !== 'CREATED' && po.status !== 'Created/New' && po.status !== 'PENDING_APPROVAL' && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <AlertTriangle className="h-5 w-5" />
                Review Required
              </CardTitle>
              <CardDescription className="text-blue-600">
                Please review the purchase order details below before taking any approval actions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleReviewPO}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Eye className="h-4 w-4 mr-2" />
                I Have Reviewed the PO Details
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Approval Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Approval Actions
            </CardTitle>
            <CardDescription>
              {hasReviewed 
                ? (po?.status === 'CREATED' || po?.status === 'Created/New' || po?.status === 'PENDING_APPROVAL'
                  ? "Review the purchase order details and take appropriate action"
                  : "Take appropriate action based on your review")
                : "Review the purchase order details above first"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => handleApprovalAction('approve')}
                disabled={processing || !hasReviewed || (po.status !== 'CREATED' && po.status !== 'Created/New' && po.status !== 'NEEDS_REVISION')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve PO
              </Button>
              
              <Button
                onClick={() => handleApprovalAction('reject')}
                disabled={processing || !hasReviewed || (po.status !== 'CREATED' && po.status !== 'Created/New' && po.status !== 'NEEDS_REVISION')}
                variant="destructive"
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject PO
              </Button>
              
              <Button
                onClick={() => setShowRemarks(true)}
                disabled={processing || !hasReviewed || (po.status !== 'CREATED' && po.status !== 'Created/New' && po.status !== 'NEEDS_REVISION')}
                variant="outline"
                className="flex-1"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Request Changes
              </Button>

              {/* Edit Button - Only visible for NEEDS_REVISION status */}
              {po.status === 'NEEDS_REVISION' && (
                <Button
                  onClick={handleEditPO}
                  disabled={processing}
                  variant="outline"
                  className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit PO
                </Button>
              )}
            </div>

            {/* Remarks Section */}
            {showRemarks && (
              <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                <Label htmlFor="remarks" className="text-sm font-medium">
                  Remarks for Changes Required
                </Label>
                <Textarea
                  id="remarks"
                  placeholder="Please specify what changes are required for this purchase order..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="mt-2"
                  rows={4}
                />
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleApprovalAction('request_changes')}
                    disabled={processing || !remarks.trim()}
                    variant="outline"
                    size="sm"
                  >
                    Submit Changes Request
                  </Button>
                  <Button
                    onClick={() => {
                      setShowRemarks(false);
                      setRemarks('');
                    }}
                    variant="ghost"
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Remarks */}
            {po.approvalRemarks && (
              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Previous Remarks:</strong> {po.approvalRemarks}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Items and Stock Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Items & Stock Analysis
            </CardTitle>
            <CardDescription>
              Review requested items and current stock levels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {po.items.map((item: any, index: number) => {
                const currentStock = getCurrentStock(item.skuId);
                const stockStatus = getStockStatus(currentStock, item.quantity);
                const StockIcon = stockStatus.icon;
                
                return (
                  <div key={item.id || index} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Package className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{getSKUName(item.skuId)}</h4>
                            <p className="text-sm text-muted-foreground">{getSKUCode(item.skuId)}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Requested Quantity</p>
                            <p className="font-semibold">{item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Unit Price</p>
                            <p className="font-semibold">{formatCurrency(item.unitPrice)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total Price</p>
                            <p className="font-semibold">{formatCurrency(item.totalPrice)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Current Stock</p>
                            <div className={`flex items-center gap-2 ${stockStatus.color}`}>
                              <StockIcon className="h-4 w-4" />
                              <span className="font-semibold">{currentStock}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`ml-4 p-3 rounded-lg ${stockStatus.bg}`}>
                        <div className="flex items-center gap-2">
                          <StockIcon className={`h-4 w-4 ${stockStatus.color}`} />
                          <span className={`text-sm font-medium ${stockStatus.color}`}>
                            {stockStatus.status === 'sufficient' ? 'Stock Available' :
                             stockStatus.status === 'low' ? 'Low Stock' : 'Out of Stock'}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${stockStatus.color}`}>
                          {stockStatus.status === 'sufficient' ? 'Sufficient inventory available' :
                           stockStatus.status === 'low' ? `${currentStock} units remaining` : 'No stock available'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <Separator className="my-6" />
            
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{po.items.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(po.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PO Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Purchase Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">PO Number</p>
                  <p className="font-semibold">{po.poNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
                  <p className="font-semibold text-lg">{formatCurrency(po.totalAmount)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Created Date</p>
                  <p>{new Date(po.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Required By</p>
                  <p>{po.deliveryDetails?.expectedDate ? new Date(po.deliveryDetails.expectedDate).toLocaleDateString() : 'Not specified'}</p>
                </div>
              </div>
              
              {po.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Notes</p>
                  <p className="text-sm bg-muted/50 p-3 rounded">{po.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vendor Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Vendor Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {vendor ? (
                <>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Vendor Name</p>
                    <p className="font-semibold">{vendor.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Vendor Code</p>
                    <p>{vendor.vendorCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Contact Email</p>
                    <p>{vendor.contactDetails.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Contact Phone</p>
                    <p>{vendor.contactDetails.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Performance Rating</p>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{vendor.performanceRating}</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-3 h-3 rounded-full ${
                              i < Math.floor(vendor.performanceRating)
                                ? 'bg-yellow-400'
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">Vendor information not available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delivery Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Delivery Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Delivery Address</p>
                <p className="text-sm bg-muted/50 p-3 rounded">{po.deliveryDetails?.address || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Delivery Instructions</p>
                <p className="text-sm bg-muted/50 p-3 rounded">{po.deliveryDetails?.instructions || 'No special instructions'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approval History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Approval History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {approvalHistory.map((entry, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {entry.action === 'created' ? 'PO Created' : 
                       entry.action === 'approved' ? 'PO Approved' :
                       entry.action === 'rejected' ? 'PO Rejected' : 'Changes Requested'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(entry.performedAt).toLocaleString()}
                    </p>
                    {entry.remarks && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        "{entry.remarks}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* PO Edit Modal */}
        {showEditModal && (
          <POEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            poData={po}
            remarks={po.approvalRemarks || 'No specific remarks provided'}
            onSave={handleSavePO}
            vendors={state.vendors}
            skus={state.skus}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default POApprovalPage;
